import { clone, get, isPlainObject, mapValues, set } from 'lodash';
import { deepObjHasTruthyNonObjValue } from 'core/utils/objects';
import { MetaData } from 'store/template/template';
import addPathPrefix from 'utils/addPathPrefix';
import {
    EORI_REGEX,
    getPatternDigitsAfterComma,
    getFloatValidationMessage,
    invalidEoriMessage,
    EMAIL_REGEX,
    isTemplate,
} from 'views/declarations/utils/validation-utils';

/**
 * @type {Object} ValidationSchema
 * @property {boolean} stopAtNull - Allow for validation to stop on null element. Mainly used for arrays.
 * @property {boolean} stopAtEmpty - Like `stopAtNull`, but an element every one of whose values is
 * empty counts as absent too, not just a literal null. For an optional element the user never filled
 * in, clearing the last field by hand leaves an object of nulls behind rather than removing it, and
 * without this the element is walked into and every field it requires once present is reported as
 * missing. Emptiness is truthiness at the leaves, so a value of `0` or `false` reads as empty -
 * opt in only where that cannot happen.
 */
export interface ValidationSchema<
    TChildValidators extends Record<string, Validator[] | ValidationSchema | ValidationSchema[]> | string = any
> {
    childValidators?: TChildValidators extends string
        ? { [K in TChildValidators]: Validator[] | ValidationSchema | ValidationSchema[] }
        : TChildValidators;
    selfValidators?: Validator[] | ValidationSchema | ValidationSchema[];
    arrayItemValidators?: Validator[] | ValidationSchema | ValidationSchema[];
    stopAtNull?: boolean;
    stopAtEmpty?: boolean;
}

export type ExtractValidationSchema<TValidationSchema extends ValidationSchema<any>> = ValidationSchema<
    TValidationSchema['childValidators']
>;

export class FormModel<TValues extends Record<string, any>> {
    private values: TValues;

    constructor(state: TValues) {
        this.values = state;
    }

    getValues() {
        return this.values;
    }

    get(path: string): Record<string, any> | string | undefined {
        if (path === '') return this.values;

        return get(this.values, path);
    }
}

const getTemplateFieldMeta = (path: string, formModel: FormModel<any>): MetaData | undefined => {
    if (!isTemplate(path)) {
        return undefined;
    }

    const pathSplit = path.split('.');
    const form = pathSplit.at(0);
    const templateIndependentFieldPath = pathSplit.slice(2).join('.');

    const meta = formModel.get(`${form}.meta`) as Record<string, MetaData> | undefined;
    return meta?.[templateIndependentFieldPath];
};

const isTemplateViewable = (path: string, formModel: FormModel<any>) => {
    return !!getTemplateFieldMeta(path, formModel)?.isViewable;
};

// A user can fill in an editable field themselves, so only a field the user sees but cannot edit
// needs a default from the template author. Same rule as `templateTest` in validation-utils.
const isTemplateViewableAndLocked = (path: string, formModel: FormModel<any>) => {
    const fieldMeta = getTemplateFieldMeta(path, formModel);
    return !!fieldMeta?.isViewable && !fieldMeta.isEditable;
};

// =============================== Validators ===============================
export type Validator = (
    path: string,
    formModel: FormModel<any>
) => Promise<string | (string | undefined)[] | { [key: string]: string } | undefined>;
// Marks validators that report a missing value, so a form that may be left partial can drop them
// (see `withoutRequiredValidators`) while keeping the format and length validators.
const markAsRequiredRule = <T extends Validator>(validator: T): T => Object.assign(validator, { isRequiredRule: true });

const requiredFactory = () =>
    markAsRequiredRule(async (path: string, formModel: FormModel<any>) => {
        if (isTemplate(path)) {
            return undefined;
        }

        const value = formModel.get(path);
        if (value == null || value === '') {
            return 'Field is required';
        }
        return undefined;
    });

const maxLengthFactory = (number: number) => async (path: string, formModel: FormModel<any>) => {
    const value = formModel.get(path);
    const length = value?.toString().length;
    if (length && length > number) {
        return `Must be ${number} characters or less`;
    }
    return undefined;
};
const minFactory = (number: number) => async (path: string, formModel: FormModel<any>) => {
    if (isTemplate(path) && !isTemplateViewable(path, formModel)) {
        return undefined;
    }

    const value = formModel.get(path);
    if (isNaN(Number(value))) return undefined;
    if (Number(value) < number) {
        return `Must be greater than or equal to ${number}`;
    }
    return undefined;
};
const exactFactory = (number: number) => async (path: string, formModel: FormModel<any>) => {
    const value = formModel.get(path);

    if (!value?.toString().length) return undefined;

    if (value?.toString().length !== number) {
        return `Must be exactly ${number} characters`;
    }

    return undefined;
};
const numberFactory = () => async (path: string, formModel: FormModel<any>) => {
    const value = formModel.get(path);
    if (value && isNaN(Number(value))) {
        return `Must be a number`;
    }
    return undefined;
};
const minLengthFactory = (minLength: number) =>
    markAsRequiredRule(async (path: string, formModel: FormModel<any>) => {
        if (isTemplate(path)) {
            return undefined;
        }

        const value = formModel.get(path);
        if (!value || (Array.isArray(value) && value.length < minLength)) {
            return `At least ${minLength} item(s) required`;
        }
        return undefined;
    });

const hackArrayMinLengthFactory = (key: string, minLength: number, onField?: boolean) =>
    markAsRequiredRule(async (path: string, formModel: FormModel<any>) => {
        if (isTemplate(path) && !isTemplateViewable(path, formModel)) {
            return undefined;
        }

        const fieldPath = addPathPrefix(path, key);
        const value = formModel.get(fieldPath);
        if (!value || !value.length || value.length < minLength)
            return { [`${fieldPath}${onField ? '.0' : ''}`]: `At least ${minLength} item(s) required` };

        return undefined;
    });

const eoriFactory = () => async (path: string, formModel: FormModel<any>) => {
    const value = formModel.get(path) as string;
    let message = invalidEoriMessage;

    if (!value) return undefined;

    const hasValidPattern = value.toString().match(EORI_REGEX);

    if (hasValidPattern) {
        try {
            const isValid = await window.EoriService.checkEori(path, value);

            if (isValid) {
                return undefined;
            }
        } catch (error: any) {
            message = error.reason;
        }
    }

    return message;
};
const floatFactory = (beforeComma: number, afterComma: number) => async (path: string, formModel: FormModel<any>) => {
    const value = formModel.get(path);
    const message = getFloatValidationMessage(beforeComma, afterComma);
    const patternDigitsAfterComma = getPatternDigitsAfterComma(beforeComma, afterComma);

    if (!value?.toString().length) return undefined;

    if (!value?.toString().match(patternDigitsAfterComma)) {
        return message;
    }

    return undefined;
};
const arrayFactory =
    (options?: { min?: number; max?: number; unique?: boolean }) => async (path: string, formModel: FormModel<any>) => {
        const arrayPath = path.split('.')[0];
        const value = formModel.get(arrayPath);

        if (!Array.isArray(value)) return undefined;

        if (options?.max && value.length > options.max) {
            return `Maximum of ${options.max} item(s) allowed.`;
        }

        if (options?.min && value.length < options.min) {
            return `At least ${options.min} item(s) required.`;
        }

        if (options?.unique) {
            const uniqueSet = new Set(value);
            if (uniqueSet.size !== value.length) {
                return 'Items must be unique.';
            }
        }

        return undefined;
    };

const templateFactory = () => async (path: string, formModel: FormModel<any>) => {
    if (!isTemplateViewableAndLocked(path, formModel)) {
        return undefined;
    }

    const value = formModel.get(path);
    if (value == null || value === '') {
        return 'Field is required';
    }
    return undefined;
};

const emailFactory = () => async (path: string, formModel: FormModel<any>) => {
    const value = formModel.get(path) as string;

    if (!value) return undefined;

    if (value && !value.match(EMAIL_REGEX)) return 'Invalid email.';
    else return undefined;
};

const regexFactory = (regex: RegExp, message: string) => async (path: string, formModel: FormModel<any>) => {
    const value = formModel.get(path) as string;

    if (!value) return undefined;

    if (!value.toString().match(regex)) {
        return message;
    }

    return undefined;
};

const booleanFactory = () => async (path: string, formModel: FormModel<any>) => {
    const value = formModel.get(path);
    if (!value) return undefined;

    if (typeof value !== 'boolean') {
        return 'Value must be true or false.';
    }

    return undefined;
};

export const validators = {
    required: requiredFactory,
    maxLength: maxLengthFactory,
    min: minFactory,
    exact: exactFactory,
    number: numberFactory,
    eori: eoriFactory,
    float: floatFactory,
    array: arrayFactory,
    minLength: minLengthFactory,
    hackArrayMinLength: hackArrayMinLengthFactory,
    template: templateFactory,
    email: emailFactory,
    regex: regexFactory,
    boolean: booleanFactory,
};

const isValidator = (value: any): value is Validator => {
    return value instanceof Function;
};
const isValidationSchema = (value: any): value is ValidationSchema => {
    return (
        value &&
        ((value as ValidationSchema | undefined)?.childValidators !== undefined ||
            (value as ValidationSchema | undefined)?.selfValidators !== undefined ||
            (value as ValidationSchema | undefined)?.stopAtNull !== undefined)
    );
};
const instanceValidate = async (formModel: FormModel<any>, schema: ValidationSchema | undefined, currentKey = '') => {
    let errors: Record<string, string | (string | undefined)[]> = {};

    const doValidate = async (
        validators: Validator[] | ValidationSchema | ValidationSchema[] | undefined,
        key: string
    ): Promise<boolean | undefined> => {
        if (Array.isArray(validators)) {
            for (let validator of validators) {
                if (isValidator(validator)) {
                    const error = await validator(key, formModel);
                    if (error) {
                        if (typeof error === 'string' || Array.isArray(error)) {
                            errors[key] = error;
                            return true;
                        } else {
                            // hack for array validation
                            // This is pretty special case, for validators that return object with errors for each string and array.
                            // We are using this to validate presence of at least one item in array with hackArrayMinLengthFactory
                            Object.assign(errors, error);
                            break;
                        }
                    }
                } else {
                    const error = await validate(formModel, validator, key);
                    if (Object.keys(error).length > 0) {
                        Object.assign(errors, error);
                    }
                }
            }
        } else if (isValidationSchema(validators)) {
            const error = await validate(formModel, validators, key);
            Object.assign(errors, error);
        }
    };

    const handleValidators = async (
        validators: Validator[] | ValidationSchema | ValidationSchema[] | undefined,
        key: string
    ) => {
        // if the value is array we call a recursive validation to validate each item
        if (Array.isArray(formModel.get(key))) {
            if (isValidationSchema(validators)) {
                const hasError = await doValidate(validators.selfValidators, key);
                if (hasError) return;
                // Stop here if there is an error
                const { selfValidators, ...exceptSelfValidators } = validators;
                const error = await validate(formModel, exceptSelfValidators, key);
                Object.assign(errors, error);
            } else {
                const error = await validate(formModel, { selfValidators: validators }, key);
                Object.assign(errors, error);
            }
            return;
        }

        await doValidate(validators, key);
    };

    const nodeValue = formModel.get(currentKey);
    const absent =
        (schema?.stopAtNull && nodeValue == null) ||
        (schema?.stopAtEmpty && !deepObjHasTruthyNonObjValue(nodeValue as Record<any, any> | undefined));

    if (!absent) {
        for (const [key, schemaOrValidators] of Object.entries(schema?.childValidators ?? {})) {
            await handleValidators(
                schemaOrValidators as Record<string, Validator[] | ValidationSchema | ValidationSchema[]>,
                addPathPrefix(currentKey, key)
            );
        }
    }
    await handleValidators(schema?.selfValidators, currentKey);
    await handleValidators(schema?.arrayItemValidators, currentKey);

    return errors;
};
export const validate = async (formModel: FormModel<any>, schema: ValidationSchema | undefined, currentKey = '') => {
    let errors: Record<string, string | string[]> = {};

    const value = formModel.get(currentKey);

    if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
            const error = await instanceValidate(formModel, schema, addPathPrefix(currentKey, i.toString()));
            Object.assign(errors, error);
        }
    } else {
        const error = await instanceValidate(formModel, schema, currentKey);
        Object.assign(errors, error);
    }

    return errors;
};
export const transformErrorsForFormik = (errors: Record<string, string | string[]>) => {
    let transformedErrors: Record<string, any> = {};
    for (const [key, value] of Object.entries(errors)) {
        transformedErrors = set(transformedErrors, key, value);
    }
    return transformedErrors;
};

const isValidatorList = (value: unknown): value is Validator[] =>
    Array.isArray(value) && value.every((item) => isValidator(item));

const withTemplateValidator = (validatorList: Validator[]) => [...validatorList, validators.template()];

type SchemaOrValidators = ValidationSchema | Validator[] | ValidationSchema[] | undefined;

/**
 * Appends the template validator to every field's list of validators. Validator functions are never
 * copied, only listed, because lodash `clone` turns a function into `{}` and the validator is lost.
 * `selfValidators` validate the node as a whole, so they are kept as they are.
 */
export const injectTemplateValidations = (schema: SchemaOrValidators): SchemaOrValidators => {
    if (isValidatorList(schema)) return withTemplateValidator(schema);
    if (Array.isArray(schema)) return schema.map((item) => injectTemplateValidations(item) as ValidationSchema);
    if (!isPlainObject(schema)) return schema;

    const result: any = clone(schema);
    const isValidationSchemaNode = result.childValidators || result.selfValidators;

    if (isValidationSchemaNode) {
        if (result.childValidators) result.childValidators = injectTemplateValidations(result.childValidators);
        if (result.selfValidators && !isValidatorList(result.selfValidators)) {
            result.selfValidators = injectTemplateValidations(result.selfValidators);
        }
        return result;
    }

    // A map of field name to validators or nested schema
    for (const key of Object.keys(result)) {
        result[key] = injectTemplateValidations(result[key]);
    }
    return result;
};

const isRequiredRule = (validator: Validator) => (validator as Validator & { isRequiredRule?: boolean }).isRequiredRule;
const SCHEMA_NODE_KEYS = ['childValidators', 'selfValidators', 'arrayItemValidators'] as const;

/**
 * Copy of the schema without the validators that report a missing value (`required`, `minLength`,
 * `hackArrayMinLength`), for forms that may be left partial. Format and length validators stay.
 * Validator functions are only listed, never copied, and the input is not mutated.
 */
export const withoutRequiredValidators = (schema: SchemaOrValidators): SchemaOrValidators => {
    if (Array.isArray(schema)) {
        const kept = (schema as (Validator | ValidationSchema)[]).filter(
            (item) => !(isValidator(item) && isRequiredRule(item))
        );
        return kept.map((item) =>
            isValidator(item) ? item : (withoutRequiredValidators(item) as ValidationSchema)
        ) as Validator[] | ValidationSchema[];
    }
    if (!isPlainObject(schema)) return schema;

    const result: any = clone(schema);
    for (const key of SCHEMA_NODE_KEYS) {
        if (key === 'childValidators' && result[key]) {
            result[key] = mapValues(result[key], (child) => withoutRequiredValidators(child));
        } else if (result[key]) {
            result[key] = withoutRequiredValidators(result[key]);
        }
    }
    return result;
};

export default validate;
